import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import OrangeButton from "../../../../../components/buttons/OrangeButton";
import Label from "../../../../../components/form_fields/Label";
import { useDropzone } from "react-dropzone";
import { IoImageOutline } from "react-icons/io5";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Avatar, Popover } from "@mui/material";
import { MdModeEdit } from "react-icons/md";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { uploadOneDocument } from "../../../../../services/document.service";
import useUserStore from "../../../../../state-management/useUserStore";
import { UpdateOrganizationPartialDto } from "../../../../../models/organization.dto";
import { useGetCountry } from "../../../../../services/country.service";
import { useUpdateOwnOrganization } from "../../../../../services/organization.service";
import { PERMISSIONS, PermissionChecker } from "../../../../../helpers/usePermissionManager";

const OrganizationDetails = () => {
	const { currentUser, setCurrentUser } = useUserStore();
	const [isEditable, setIsEditable] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [image, setImage] = useState("");
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	
	const { data: countries } = useGetCountry({
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "name:asc" as any,
	});

	const { getRootProps, getInputProps } = useDropzone({
		accept: {
			"image/jpg": [".jpg"],
			"image/jpeg": [".jpeg"],
			"image/png": [".png"],
		},
		maxFiles: 1,
		onDrop: async (acceptedFiles) => {
			const reader = new FileReader();
			reader.readAsDataURL(acceptedFiles[0]);
			reader.onloadend = () => {
				const base64 = reader.result;
				setImage(base64 as string);
			};
			setFile(acceptedFiles[0]);
			setIsEditable(true);
		},
	});

	const {
		register,
		handleSubmit,
		control,
		reset,
		clearErrors,
		formState: { errors },
	} = useForm<UpdateOrganizationPartialDto>({
		defaultValues: {
			name: currentUser.user.organization?.name,
			contactEmail: currentUser.user.organization?.contactEmail,
			contactMsisdn: currentUser.user.organization?.contactMsisdn,
			address: currentUser.user.organization?.address,
		}
	});

	const { mutate: updateOrganization } = useUpdateOwnOrganization();
	const [uploading, setUploading] = useState(false);

	const onSubmit: SubmitHandler<UpdateOrganizationPartialDto> = async (data) => {
		setUploading(true);

		if (file) {
			const formData = new FormData();
			formData.append('file', file);

			const uploadedFile = await uploadOneDocument(formData, true);
			data.logoUrl = uploadedFile?.data?.url;
		}

		updateOrganization(
			{ payload: data },
			{
				onSuccess: (updatedUser) => {
					setCurrentUser({
						...currentUser,
						user: {
							...(currentUser as any).user,
							...updatedUser.data
						},
					});
					setIsEditable(false);
				},
				onSettled: () => setUploading(false),
			}
		)
	};

	return (
		<div className="mb-4">
			<div className="border-b-[1.5px] pb-3 flex w-full justify-between font-semibold text-sm">
				<h2 className="font-semibold text-sm">Organization Details</h2>
				<PermissionChecker permissions={[PERMISSIONS.ORGANIZATIONS_WRITE]}>
					<button
						className={`border-none text-primary-blue text-sm ${
							isEditable ? "hidden" : ""
						}`}
						onClick={() => setIsEditable(true)}
					>
						Edit
					</button>
				</PermissionChecker>
			</div>
			<div className="flex flex-col gap-2 mt-5">
				<div className="text-sm">Organization Logo</div>
				<div className="relative inline-flex">
					<button
						className="absolute -top-2 left-[166px] z-10 bg-white rounded-full shadow-md border border-gray-400 p-1 flex items-center justify-center"
						onClick={(e) => setAnchorEl(e.currentTarget)}
						disabled={!isEditable}
					>
						<MdModeEdit className="text-lg hover:opacity-90" />
					</button>
					<Avatar
						variant="square"
						sx={{ borderRadius: "5px", width: 180, height: 180 }}
						alt="company"
						src={image ? image : currentUser.user.organization?.logoUrl}
						className="border border-gray-300"
					/>
				</div>
			</div>
			<div className="grid grid-cols-2 gap-x-4 gap-y-6 mt-8">
				<div className="w-full">
					<Label title="Organization Name" for="organizationName" />
					<input
						type="text"
						{...register("name", {
							required: "Enter your organization name",
						})}
						id="organizationName"
						placeholder="Enter organization name"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
							errors.name
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						disabled={!isEditable}
					/>
					{errors.name && (
						<p className="text-red-500 text-sm mt-1">
							{errors.name.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Contact Email" for="contactEmail" />
					<input
						type="email"
						{...register("contactEmail", {
							required: "Enter your email",
							pattern: {
								value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
								message: "Enter a valid email address",
							},
						})}
						id="contactEmail"
						placeholder="Enter email address"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
							errors.contactEmail
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						disabled={!isEditable}
					/>
					{errors.contactEmail && (
						<p className="text-red-500 text-sm mt-1">
							{errors.contactEmail.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Country" for="countryId" />
					<select
						className="w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm border-none"
						disabled
					>
						<option value="">
							Select Country
						</option>
						{countries?.data?.map((country) => (
							<option key={country.id} value={country.id} >
								{country.name}
							</option>
						))}
					</select>
				</div>
				<div className="w-full">
					<Label title="Address" for="address" optional />
					<input
						placeholder={"Enter address"}
						type="text"
						{...register("address", {
							required: false,
						})}
						id="address"
						className="w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm border-none"
						disabled={!isEditable}
					/>
				</div>
				<div className="w-full">
					<Label title="Telephone" for="contactMsisdn" optional />
					<Controller
						name={"contactMsisdn"}
						control={control}
						rules={{ required: false }}
						render={({ field }) => (
							<div className="w-full bg-secondary-gray rounded-lg mt-2 flex items-center justify-center border-none">
								<PhoneInput
									{...field}
									country={"gh"}
									disabled={!isEditable}
									inputStyle={{
										height: errors.contactMsisdn ? "50px" : "48px",
										border: "none",
										backgroundColor: "#F2F2F4",
										color: "black",
										boxShadow: "none",
										width: "95%",
										margin: "0px 0px 0px 15px",
										borderRadius: "10px",
									}}
									containerStyle={{
										margin: "4px 0px",
										height: errors.contactMsisdn ? "50px" : "48px",
										border: "none",
										backgroundColor: "#F2F2F4",
										boxShadow: "none",
										borderRadius: "10px",
									}}
									buttonStyle={{
										border: "none",
										backgroundColor: "#F2F2F4",
										boxShadow: "none",
										padding: "0px 10px",
									}}
								/>
							</div>
						)}
					/>
				</div>
				{isEditable && (
					<div className="w-full flex items-center gap-3 justify-end mt-16">
						<button
							className="px-12 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
							onClick={() => {
								reset();
								clearErrors();
								setImage("");
								setIsEditable(false);
							}}
							disabled={uploading}
						>
							Cancel
						</button>
						<OrangeButton
							title={uploading ? "Uploading..." : "Save"}
							className={"px-12 hover:opacity-95"}
							onClick={handleSubmit(onSubmit)}
							disabled={uploading}
						/>
					</div>
				)}
			</div>

			<Popover
				id={Boolean(anchorEl) ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: 150,
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center py-3 space-y-2">
					<div
						className="text-primary-blue flex items-center p-3 cursor-pointer hover:opacity-80 hover:bg-blue-50 rounded-lg bg-slate-50"
						{...getRootProps()}
					>
						<input {...getInputProps()} />
						<IoImageOutline />{" "}
						<span className="text-xs ml-2">Change Photo</span>
					</div>
					{/* <div
						onClick={() => {
							setOpenDeletePhotoConfirmation(true);
						}}
						className="flex items-center space-x-2 p-3 text-red-400 cursor-pointer hover:opacity-80 hover:bg-red-50 rounded-lg bg-slate-50"
					>
						<MdOutlineFolderDelete />
						<span className="border-none text-xs">Remove Photo</span>
					</div> */}
				</div>
			</Popover>
		</div>
	);
};

export default OrganizationDetails;
